<script setup>
import { 
	ref,
	computed,
	watch,
	onMounted, 
	nextTick,
} from 'vue'

import { useInspectionStore } from '@/stores/inspection-store'
import { damageNumbers, hasDamagePos } from '@/modules/util/damage-number'

import splatEstate from '@/assets/splats/splat-estate.png'
import splatHatchback from '@/assets/splats/splat-hatchback.png'
import splatHGVTractor from '@/assets/splats/splat-hgv-tractor.png'
import splatHGV from '@/assets/splats/splat-hgv.png'
import splatLCV from '@/assets/splats/splat-lcv.png'
import splatPickup from '@/assets/splats/splat-pickup.png'
import splatTrailer from '@/assets/splats/splat-trailer.png'
import splatDefault from '@/assets/splats/splat.png'

const splatMap = {
	'splat-estate': splatEstate,
	'splat-hatchback': splatHatchback,
	'splat-hgv-tractor': splatHGVTractor,
	'splat-hgv': splatHGV,
	'splat-lcv': splatLCV,
	'splat-pickup': splatPickup,
	'splat-trailer': splatTrailer,
	'splat': splatDefault,
}

const splatOptions = Object.keys(splatMap).map(option => ({
	title: option.split('-').slice(1).join(' ').toUpperCase() || 'DEFAULT',
	value: option,
}))

const store = useInspectionStore()
const props = defineProps(['modelValue', 'height', 'width', 'readonly', 'splat', 'showAll', 'number'])
const emit = defineEmits(['update:modelValue', 'update:splat'])
const container = ref(null)
const current = ref({ x: props.modelValue?.x, y: props.modelValue?.y })

watch(() => [props.modelValue?.x, props.modelValue?.y], ([x, y]) => {
	current.value = { x, y }
})

const activeSplat = computed(() => {
	return splatMap[props.splat]
})

const markers = computed(() => {
	if (!props.showAll)
		return []

	const items = store.currentDamageItems || []
	const numbers = damageNumbers(items)

	return items.filter(hasDamagePos).map(item => ({
		item,
		x: item.damagemappos.x,
		y: item.damagemappos.y,
		number: numbers.get(item),
	}))
})

const updateDamageMapPos = async (event) => {
	if (props.readonly) {
		return
	}
	
	await nextTick()
    
	const { offsetX, offsetY, target } = event
	const x = Math.round((offsetX / target.width) * 100)
	const y = Math.round((offsetY / target.height) * 100)

	current.value = { x, y }
	emit('update:modelValue', { x, y })
}

onMounted(() => {
	if (props.height)
		container.value.style.height = props.height
	if (props.width)
		container.value.style.width = props.width
})
</script>

<template>
<!-- inherits extraneous props for class and style-->
<div
	ref="container"
	:class="`${$attrs.class} position-relative`"
	:style="$attrs.style"
>
	<img
		:src="activeSplat"
		alt="Splash Damage Plot"
		class="w-100 h-100"
		@click="updateDamageMapPos"
	>
	<div
		v-for="marker in markers"
		:key="marker.number"
		class="damage-marker bg-primary"
		:style="{ left: `${marker.x}%`, top: `${marker.y}%` }"
	>
		{{ marker.number }}
	</div>
	<div
		v-if="current.x && current.y"
		class="damage-marker bg-success"
		:style="{ left: `${current.x}%`, top: `${current.y}%` }"
	>
		{{ number }}
	</div>
</div>

<v-select
	v-if="!readonly"
	:model-value="splat"
	:items="splatOptions"
	density="compact"
	class="w-100 w-sm-25"
	@update:model-value="v => emit('update:splat', v)"
/>
</template>

<style lang="scss" scoped>
.damage-marker {
	position: absolute;
	transform: translate(-50%, -50%);
	min-width: 22px;
	height: 22px;
	padding: 0 5px;
	border-radius: 11px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: 700;
	line-height: 1;
	box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.4);
	pointer-events: none;
}
</style>